import type { en } from "./en.ts";

export const fr: typeof en = {
    Navigation: {
        "account-settings": {
            label: "Paramètres du compte",
            account: {
                label: "Compte",
                general: { label: "Général" },
                appearance: { label: "Apparence" }
            },
            agent: {
                label: "Agent",
                "agent-preferences": { label: "Préférences de l'agent" },
                skills: { label: "Compétences" },
                "skill-detail": { label: "Compétence" },
                "connected-apps": { label: "Applications connectées" }
            }
        }
    },
    AccountGeneralPage: {
        title: "Général",
        description: "Les paramètres généraux du compte peuvent être gérés dans Workleap Classic."
    },
    AgentPreferencesPage: {
        description: "Les préférences de l'agent peuvent être gérées dans Workleap AI.",
        goThereNow: "Y aller maintenant",
        modalEyebrow: "À noter",
        modalTitle: "Un petit détour",
        modalBody: "Nous vous redirigerons vers Workleap AI pour gérer vos préférences de l'agent. Rien ne sera perdu.",
        modalCancelLabel: "Annuler",
        modalContinueLabel: "Continuer dans Workleap AI"
    },
    ConnectedAppsPage: {
        managedInClassic: "Les applications connectées peuvent être gérées dans Workleap Classic."
    },
    ConnectedAppsList: {
        description: "Connecter, gérer et mettre à jour les applications que vous utilisez avec Workleap IA, le tout en un seul endroit.",
        totalCount_one: "{{count}} application",
        totalCount_other: "{{count}} applications",
        connectedCount_one: "{{count}} connectée",
        connectedCount_other: "{{count}} connectées",
        filteredCount_one: "{{count}} application",
        filteredCount_other: "{{count}} applications",
        error: "Impossible de charger vos applications connectées.",
        featureNotEnabledTitle: "Non disponible pour votre organisation",
        featureNotEnabledDescription: "La connexion d'applications personnelles n'est pas encore activée ici. Contactez votre administrateur si vous en avez besoin.",
        policyDisabledTitle: "Intégrations personnelles désactivées",
        policyDisabledDescription: "Votre organisation n'autorise pas les connexions d'applications personnelles. Contactez votre administrateur pour demander l'accès.",
        policyDisabledManagePermissions: "Gérer les permissions",
        noResults: "Aucune application ne correspond à vos filtres",
        noResultsHint: "Essayez une autre recherche ou effacez un filtre.",
        noData: "Aucune application n'est encore disponible.",
        viewToggle: {
            groupLabel: "Mode d'affichage",
            listView: "Vue liste",
            cardView: "Vue cartes"
        },
        liveRegion: {
            loading: "Chargement des applications connectées"
        }
    },
    ConnectedAppRow: {
        reconnect: "Reconnecter",
        openDetail: "{{app}}, {{status}} — voir les détails"
    },
    ConnectedAppCard: {
        reconnect: "Reconnecter",
        openDetail: "{{app}}, {{status}} — voir les détails"
    },
    ConnectedAppDetailDialog: {
        unlocks: "Ce que ça débloque",
        dataGathered: "Ce que l'agent lira",
        cancel: "Annuler",
        connect: "Connecter {{app}}",
        reconnect: "Reconnecter {{app}}",
        disconnect: "Déconnecter"
    },
    ConnectedAppDetailRoute: {
        disconnectError: "Impossible de déconnecter l'application. Veuillez réessayer."
    },
    CustomMcpDetailRoute: {
        disconnectError: "Impossible de déconnecter le serveur. Veuillez réessayer."
    },
    connectedAppProviders: {
        confluence: {
            description: "Accéder et rechercher les connaissances des espaces et pages Confluence.",
            unlocks: ["Retrouver les décisions et le contexte de vos espaces sans chercher la bonne page", "Appuyer un résumé ou un rapport sur la documentation que vous tenez déjà"],
            dataGathered: ["Les pages des espaces que vous pouvez déjà ouvrir", "Les commentaires et mentions sur ces pages"]
        },
        google: {
            description: "Accéder à vos fichiers, courriels et événements de calendrier de Google.",
            unlocks: ["Voir comment votre semaine est réellement remplie, et où s'enchaînent les réunions", "Citer un document que vous possédez ou qu'on vous a partagé sans quitter la conversation", "Repérer l'engagement enfoui dans un fil de courriels"],
            dataGathered: ["Les titres, participants et durées de vos événements de calendrier", "Les documents Drive que vous possédez ou qu'on vous a partagés", "Les messages de votre propre boîte Gmail"],
            privacyNote: "Uniquement ce que votre compte Google voit déjà. Les événements marqués privés comptent dans votre charge, sans leurs détails."
        },
        granola: {
            description: "Accéder aux notes et transcriptions de vos réunions Granola.",
            unlocks: ["Transformer une réunion que vous avez enregistrée en suivis, sans réécrire vos notes", "Citer ce qui a réellement été dit en préparant la conversation suivante"],
            dataGathered: ["Les notes et transcriptions des réunions de votre propre compte Granola"],
            privacyNote: "Uniquement les réunions que vous avez enregistrées. Rien du compte Granola d'un collègue."
        },
        hubspot: {
            description: "Accéder aux contacts, entreprises et transactions de votre CRM HubSpot.",
            unlocks: ["Intégrer le contexte d'une transaction ou d'un compte dans un rapport, sans export manuel", "Citer où en est une conversation client"],
            dataGathered: ["Les contacts, entreprises et transactions que votre utilisateur HubSpot peut déjà ouvrir"],
            privacyNote: "Lecture seule. Rien n'est créé, modifié ni supprimé dans votre CRM."
        },
        jira: {
            description: "Rechercher et explorer vos éléments de travail et projets.",
            unlocks: ["Montrer ce que vous avez livré, avec les tickets qui l'attestent", "Garder l'avancement d'un objectif honnête face au travail qui bouge vraiment", "Voir où en est un sprint sans ouvrir le tableau"],
            dataGathered: ["Les tickets que vous avez créés, qui vous sont assignés ou que vous suivez", "Les changements de statut et commentaires sur ces tickets", "L'avancement des sprints de vos projets"],
            privacyNote: "Uniquement les projets auxquels vous avez déjà accès."
        },
        microsoftOutlook: {
            description: "Accéder à vos courriels et événements de calendrier de Microsoft Outlook.",
            unlocks: ["Voir comment votre semaine est réellement remplie, et où s'enchaînent les réunions", "Repérer l'engagement enfoui dans un fil de courriels", "Préparer une conversation avec le contexte de ce qui l'a précédée"],
            dataGathered: ["Les titres, participants et durées de vos événements de calendrier", "Les messages de votre propre boîte de réception"],
            privacyNote: "Uniquement votre boîte et votre calendrier. Les événements marqués privés comptent dans votre charge, sans leurs détails."
        },
        microsoftSharePoint: {
            description: "Accéder aux fichiers et pages de vos sites SharePoint.",
            unlocks: ["Citer un document d'un site où vous travaillez sans aller le chercher", "Appuyer un résumé sur les fichiers que votre équipe maintient vraiment"],
            dataGathered: ["Les fichiers et pages des sites que vous pouvez déjà ouvrir"],
            privacyNote: "Uniquement les sites et fichiers auxquels vous avez déjà accès."
        },
        microsoftTeams: {
            description: "Accéder aux messages et canaux de votre espace de travail Microsoft Teams.",
            unlocks: ["Retrouver des réponses dans les conversations passées sans remonter les canaux", "Reprendre ce qu'un fil a tranché dans un résumé ou un rapport"],
            dataGathered: ["Les messages des canaux dont vous êtes membre", "Les mentions et réponses sur vos propres publications"],
            privacyNote: "Workleap ne lit jamais les conversations privées dont vous ne faites pas partie."
        },
        notion: {
            description: "Accéder aux pages et bases de données de votre espace de travail Notion.",
            unlocks: ["Citer vos notes et documents de projet sans les recopier ailleurs", "Appuyer un résumé sur les pages que vous tenez déjà à jour"],
            dataGathered: ["Les pages et bases de données des espaces que vous pouvez déjà ouvrir", "Les commentaires et mentions sur ces pages"]
        },
        slack: {
            description: "Accéder aux messages des canaux et messages directs auxquels vous avez accès.",
            unlocks: ["Retrouver des réponses dans les conversations passées sans fouiller les canaux", "Reprendre ce qu'un fil a tranché dans un résumé ou un rapport", "Faire ressortir la reconnaissance que vos collègues ont exprimée sur votre travail"],
            dataGathered: ["Les messages des canaux publics dont vous êtes membre", "Les messages directs dont vous faites partie", "Les fichiers et liens partagés dans ces conversations"],
            privacyNote: "Workleap ne lit jamais les messages directs dont vous ne faites pas partie, ni les canaux que vous n'avez pas rejoints."
        },
        asana: {
            description: "Accéder aux tâches et projets de votre espace de travail Asana.",
            unlocks: ["Montrer ce que vous avez livré avec les tâches qui l'attestent", "Suivre où en est un projet sans ouvrir chaque tableau", "Voir ce qui bloque et qui porte la prochaine étape"],
            dataGathered: ["Les tâches des projets dont vous êtes membre", "Le statut et les jalons des projets auxquels vous avez déjà accès"]
        },
        clickup: {
            description: "Accéder aux tâches et documents de votre espace de travail ClickUp.",
            unlocks: ["Citer la tâche derrière une décision sans chercher dans les espaces", "Voir où en est un projet et ce qui le bloque"],
            dataGathered: ["Les tâches et documents des espaces que vous pouvez déjà ouvrir"]
        },
        figma: {
            description: "Accéder aux fichiers et projets de votre espace de travail Figma.",
            unlocks: ["Citer un design sur lequel vous travaillez sans quitter la conversation", "Reprendre les décisions de design et annotations dans un résumé écrit"],
            dataGathered: ["Les fichiers et projets des espaces de travail que vous pouvez déjà ouvrir"]
        },
        github: {
            description: "Accéder aux dépôts et tickets de votre compte GitHub.",
            unlocks: ["Montrer ce que vous avez livré avec les pull requests et commits qui l'attestent", "Voir où en est un projet et ce qui est ouvert en revue", "Intégrer le contexte d'un ticket dans un rapport sans changer d'onglet"],
            dataGathered: ["Les dépôts, tickets et pull requests auxquels vous avez déjà accès"],
            privacyNote: "Uniquement les dépôts que votre compte GitHub voit déjà."
        },
        gong: {
            description: "Accéder aux appels et conversations de votre espace Gong.",
            unlocks: ["Reprendre ce qui a été dit lors d'un appel dans un suivi ou un résumé", "Citer le contexte d'une transaction à partir des conversations qui l'ont construite"],
            dataGathered: ["Les appels et transcriptions auxquels vous avez déjà accès dans votre espace Gong"],
            privacyNote: "Uniquement les appels partagés avec vous ou enregistrés par votre utilisateur Gong."
        },
        intercom: {
            description: "Accéder aux conversations et contacts de votre espace Intercom.",
            unlocks: ["Intégrer le contexte des conversations client dans un rapport ou un transfert", "Citer où en est une conversation de support sans changer d'onglet"],
            dataGathered: ["Les conversations et contacts que votre utilisateur Intercom peut déjà ouvrir"],
            privacyNote: "Lecture seule. Rien n'est créé, modifié ni supprimé dans Intercom."
        },
        linear: {
            description: "Accéder aux tickets et projets de votre espace de travail Linear.",
            unlocks: ["Montrer ce qui a été livré avec les tickets qui l'attestent", "Voir où en est un cycle sans ouvrir le tableau", "Garder une feuille de route honnête face à ce qui bouge vraiment"],
            dataGathered: ["Les tickets que vous avez créés, qui vous sont assignés ou que vous suivez", "L'avancement des projets et cycles de vos équipes"]
        },
        miro: {
            description: "Accéder aux tableaux et contenus de votre espace de travail Miro.",
            unlocks: ["Citer un tableau sur lequel vous avez travaillé sans le décrire de mémoire", "Reprendre les décisions visuelles et schémas dans un résumé écrit"],
            dataGathered: ["Les tableaux des espaces de travail que vous pouvez déjà ouvrir"]
        },
        mixpanel: {
            description: "Accéder aux analyses produit et rapports de votre projet Mixpanel.",
            unlocks: ["Intégrer les données d'entonnoir et de rétention dans un rapport sans export manuel", "Appuyer une décision produit sur les métriques qui comptent"],
            dataGathered: ["Les événements, rapports et tableaux de bord des projets auxquels vous avez déjà accès"],
            privacyNote: "Lecture seule. Aucune donnée n'est modifiée dans Mixpanel."
        }
    },
    ConnectedAppsToolbar: {
        searchPlaceholder: "Rechercher une application",
        searchAriaLabel: "Rechercher parmi les applications connectées",
        filters: {
            status: "Statut",
            statusSelected: "{{count}} statuts",
            statusAriaLabel: "Filtrer par statut",
            clearFilter: "Effacer le filtre",
            categoriesAriaLabel: "Filtrer par catégorie"
        },
        resetFilters: "Réinitialiser les filtres"
    },
    connectedAppCategories: {
        communication: "Communication",
        "calendar-and-meetings": "Agenda et réunions",
        documents: "Documents",
        "project-management": "Gestion de projet",
        "engineering-and-analytics": "Ingénierie et analytique",
        design: "Design",
        "sales-and-support": "Ventes et soutien",
        "custom-server": "Serveur personnalisé"
    },
    connectedAppStatuses: {
        connected: "Connecté",
        expired: "Connexion expirée",
        missingScopes: "Autorisations manquantes",
        notConnected: "Non connecté"
    },
    Errors: {
        retry: "Réessayer",
        reassurance: "Veuillez réessayer plus tard.",
        refreshBanner: "Impossible d'actualiser — affichage des derniers résultats."
    },
    SkillsPage: {
        description: "Ajustez la façon dont l'agent exécute chaque compétence pour vous.",
        liveRegion: {
            loading: "Chargement des compétences"
        },
        features: {
            meetings: { label: "Rencontres 1:1" },
            engagement: { label: "Engagement" },
            recognition: { label: "Bonnes Vibes" },
            performance_reviews: { label: "Évaluations de performance" },
            people: { label: "Parcours employé" },
            plans: { label: "Plans" }
        }
    },
    agentSkills: {
        skills: {
            wov_engagement_report: {
                name: "Rapport Pulse",
                description: "Envoie un résumé hebdomadaire de l'évolution de l'engagement de votre équipe."
            },
            wov_help_me_reply_to_feedback: {
                name: "M'aider à répondre au feedback",
                description: "Rédige une réponse réfléchie au feedback d'un employé."
            },
            wpm_review: {
                name: "Évaluation de performance",
                description: "Rassemble les données pour une évaluation, puis rédige, retravaille et affine les réponses en respectant votre style."
            },
            wai_people_about: {
                name: "À propos d'une personne",
                description: "Rédige un court profil sourcé d'une personne, limité à ce que le lecteur peut voir."
            },
            draft_a_plan: {
                name: "Rédiger un plan",
                description: "Construit le plan qu'un moment appelle : intégration, départ, retour de congé, amélioration, développement ou relève."
            },
            wov_summarize_feedback: {
                name: "Résumer le feedback",
                description: "Cible les thèmes de votre feedback, en explique l'importance et suggère des actions concrètes."
            },
            wov_feedback_export: {
                name: "Exporter le rapport de feedback",
                description: "Transforme votre rapport de feedback en présentation ou en document prêt à partager."
            },
            wov_analyze_engagement: {
                name: "Analyser l'engagement",
                description: "Cible ce qui se dégage de vos résultats d'engagement, d'eNPS et de participation, en explique la cause et suggère les prochaines étapes."
            },
            wov_export_engagement: {
                name: "Exporter le rapport d'engagement",
                description: "Transforme votre rapport d'engagement en présentation ou en document prêt à partager."
            }
        }
    },
    SkillDetailPage: {
        personalize: {
            title: "Personnaliser",
            description: "Le comportement de l'agent lorsqu'il utilise cette compétence pour vous.",
            byDefault: "Modèles de compétence",
            byDefaultDescription: "Chacun est un ensemble d'instructions différent que l'agent peut suivre. Tout ce que vous ajoutez n'appartient qu'à vous."
        },
        liveRegion: {
            loading: "Chargement de la compétence"
        }
    },
    SkillTemplates: {
        empty: "Aucun modèle pour l'instant. Créez-en un pour ajouter une autre façon d'utiliser cette compétence.",
        emptyReadOnly: "Aucun modèle pour l'instant.",
        creationDisabled: "Votre organisation n'a pas activé les modèles personnels pour cette compétence.",
        newTemplate: "Nouveau modèle",
        newTemplateName: "Nouveau modèle",
        defaultLabel: "Par défaut",
        toggleCard: "Afficher ou masquer {{name}}",
        scopeWorkleap: "Workleap",
        scopeOrganization: "Votre organisation",
        scopeUser: "Vos modèles",
        showAll_one: "Afficher {{count}} autre modèle",
        showAll_other: "Afficher {{count}} autres modèles",
        showFewer: "Afficher moins",
        standardTemplate: "Modèle standard",
        standardTemplates: {
            wov_engagement_report: {
                name: "Rapport éclair standard"
            },
            wov_help_me_reply_to_feedback: {
                name: "Réponse standard au feedback"
            },
            wpm_review: {
                name: "Équilibrée et dans notre voix"
            }
        }
    },
    SkillResourcePanel: {
        loading: "Chargement du modèle",
        error: "Impossible de charger ce modèle.",
        retry: "Réessayer"
    },
    SkillTemplateEditor: {
        nameLabel: "Nom",
        namePlaceholder: "Nommez ce modèle",
        requiredName: "Entrez un nom de modèle.",
        nameTooLong: "Limitez le nom à {{count}} caractères.",
        contentLabel: "Instructions de l'agent",
        contentDescription: "Comment l'agent doit exécuter cette compétence lorsqu'il utilise ce modèle.",
        contentPlaceholder: "Rédigez les consignes que l'agent doit suivre, en langage clair.",
        requiredContent: "Entrez les instructions du modèle.",
        contentTooLong: "Limitez les instructions à {{count}} caractères.",
        cancelNew: "Annuler",
        discard: "Abandonner",
        create: "Créer le modèle",
        saveChanges: "Enregistrer les modifications",
        delete: "Supprimer",
        cancel: "Annuler",
        deleteTitle: "Supprimer ce modèle ?",
        deleteBody: "Il ne sera plus accessible à l'agent. Cette action est irréversible.",
        createdToast: "Modèle créé.",
        createErrorToast: "Impossible de créer le modèle.",
        savedToast: "Modèle enregistré.",
        saveErrorToast: "Impossible d'enregistrer le modèle.",
        deletedToast: "Modèle supprimé.",
        deleteErrorToast: "Impossible de supprimer le modèle."
    },
    AppearancePage: {
        display: {
            title: "Affichage",
            description: "Thème et couleur d'accent utilisés dans Workleap sur cet appareil"
        }
    },
    ColorSchemeField: {
        label: "Thème",
        options: {
            light: "Clair",
            system: "Système",
            dark: "Sombre"
        }
    },
    AccentColorField: {
        label: "Couleur d'accent",
        themes: {
            "workleap-bleu": "Workleap Bleu",
            "screen-green": "Screen Green",
            "wednesday-pink": "Wednesday Pink",
            "group-chat-lilac": "Group Chat Lilac",
            "mono-neutral": "Mono Neutral"
        }
    }
};
